/**
 * Health tab (Phase 5 Action 49).
 *
 * Answers "is this tenant getting reports delivered?" over a rolling 7-day
 * window. Two chunks:
 *   • Delivery success rate — counts of delivered vs failed, with a
 *     color-banded rate. Zero attempts renders as "no signal" (em-dash)
 *     rather than 100%; a silent tenant isn't necessarily healthy.
 *   • Last failure — most recent failed delivery with the user, week,
 *     and error message. Nothing is more useful when triaging a "why
 *     didn't customer X get their report" ticket.
 */

import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Link, useOutletContext } from 'react-router-dom';
import { fetchJson, formatDateTime, formatRelative, type TenantContext } from './shared';

interface HealthResp {
  window: { start: string; end: string; days: number };
  deliveries: {
    succeeded: number;
    failed: number;
    successRatePct: number | null;
  };
  lastFailure: {
    deliveryId: string;
    at: string;
    errorMessage: string | null;
    reportType: string;
    year: number;
    week: number;
    userEmail: string | null;
  } | null;
}

interface DeliveryRow {
  id: string;
  userId: string;
  userEmail: string | null;
  reportType: 'product' | 'customer';
  year: number;
  week: number;
  status: 'generated' | 'delivered' | 'failed';
  errorMessage: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

const DELIVERY_STATUS_BADGE: Record<DeliveryRow['status'], string> = {
  generated: 'bg-amber-100 text-amber-800',
  delivered: 'bg-emerald-100 text-emerald-800',
  failed:    'bg-red-100 text-red-800',
};

function successRateBand(pct: number | null): string {
  if (pct === null) return 'text-slate-400';
  if (pct >= 95) return 'text-emerald-700';
  if (pct >= 75) return 'text-amber-700';
  return 'text-red-700';
}

export default function Health() {
  const { tenant } = useOutletContext<TenantContext>();

  const q = useQuery({
    queryKey: ['tenant-health', tenant.id],
    queryFn: () => fetchJson<{ data: HealthResp }>(`/api/tenants/${tenant.id}/health`),
  });

  const deliveriesQ = useQuery({
    queryKey: ['tenant-deliveries', tenant.id],
    queryFn: () => fetchJson<{ data: { deliveries: DeliveryRow[] } }>(`/api/tenants/${tenant.id}/deliveries`),
  });

  if (q.isLoading) {
    return <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading health…</div>;
  }
  if (q.isError || !q.data) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Could not load health: {q.error instanceof Error ? q.error.message : 'unknown error'}
      </div>
    );
  }

  const h = q.data.data;

  return (
    <>
      <section className="mb-6 rounded-md border border-slate-200 bg-white">
        <header className="border-b border-slate-200 px-6 py-3">
          <h2 className="text-sm font-semibold text-slate-900">
            Delivery success — last {h.window.days} days
          </h2>
        </header>
        <div className="grid grid-cols-3 divide-x divide-slate-200">
          <Stat
            label="Success rate"
            value={h.deliveries.successRatePct === null ? '—' : `${h.deliveries.successRatePct}%`}
            valueClass={clsx('text-3xl font-semibold', successRateBand(h.deliveries.successRatePct))}
            sub={h.deliveries.successRatePct === null ? 'No delivery attempts' : 'across delivered + failed'}
          />
          <Stat
            label="Delivered"
            value={h.deliveries.succeeded.toString()}
            valueClass="text-3xl font-semibold text-slate-900"
            sub="successful weekly reports"
          />
          <Stat
            label="Failed"
            value={h.deliveries.failed.toString()}
            valueClass={clsx('text-3xl font-semibold', h.deliveries.failed > 0 ? 'text-red-700' : 'text-slate-900')}
            sub="delivery failures"
          />
        </div>
      </section>

      <section className="mb-6 rounded-md border border-slate-200 bg-white">
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Last failure</h2>
          {h.lastFailure && (
            <Link
              to={`/inspector/deliveries/${h.lastFailure.deliveryId}`}
              className="rounded-md border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
            >
              Inspect delivery
            </Link>
          )}
        </header>
        <div className="px-6 py-4 text-sm">
          {!h.lastFailure && (
            <div className="text-slate-500">No delivery failures on record for this tenant.</div>
          )}
          {h.lastFailure && (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <dt className="text-slate-500">When</dt>
                <dd className="mt-0.5 text-slate-900">{formatDateTime(h.lastFailure.at)}</dd>
              </div>
              <div>
                <dt className="text-slate-500">User</dt>
                <dd className="mt-0.5 text-slate-900">{h.lastFailure.userEmail ?? <span className="text-slate-400">unknown</span>}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Report</dt>
                <dd className="mt-0.5 text-slate-900">
                  {h.lastFailure.reportType} · {h.lastFailure.year} week {h.lastFailure.week}
                </dd>
              </div>
              <div className="col-span-2">
                <dt className="text-slate-500">Error</dt>
                <dd className="mt-0.5 text-slate-900 font-mono whitespace-pre-wrap break-all">
                  {h.lastFailure.errorMessage ?? <span className="text-slate-400">no error message recorded</span>}
                </dd>
              </div>
            </dl>
          )}
        </div>
      </section>

      <section className="mb-6 rounded-md border border-slate-200 bg-white">
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Recent deliveries</h2>
          <span className="text-xs text-slate-500">
            {deliveriesQ.data ? `${deliveriesQ.data.data.deliveries.length} shown` : ''}
          </span>
        </header>
        <div className="text-sm">
          {deliveriesQ.isLoading && (
            <div className="px-6 py-6 text-slate-500">Loading deliveries…</div>
          )}
          {deliveriesQ.isError && (
            <div className="px-6 py-4 text-red-700">
              Could not load deliveries: {deliveriesQ.error instanceof Error ? deliveriesQ.error.message : 'unknown error'}
            </div>
          )}
          {deliveriesQ.data && deliveriesQ.data.data.deliveries.length === 0 && (
            <div className="px-6 py-8 text-center text-slate-500">
              No deliveries yet for this tenant. The weekly cron writes rows here as reports are generated.
            </div>
          )}
          {deliveriesQ.data && deliveriesQ.data.data.deliveries.length > 0 && (
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-6 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Recipient</th>
                  <th className="px-6 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Report</th>
                  <th className="px-6 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">When</th>
                  <th className="px-6 py-2 text-right text-xs font-medium uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {deliveriesQ.data.data.deliveries.map((d) => (
                  <tr key={d.id}>
                    <td className="px-6 py-3">
                      <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', DELIVERY_STATUS_BADGE[d.status])}>
                        {d.status}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-slate-900">
                      {d.userEmail ?? <span className="text-slate-400 font-mono text-xs">{d.userId.slice(0, 8)}</span>}
                    </td>
                    <td className="px-6 py-3 text-slate-700">
                      {d.reportType} · {d.year} wk {d.week}
                    </td>
                    <td className="px-6 py-3 text-slate-500">
                      {formatRelative(d.deliveredAt ?? d.createdAt)}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Link
                        to={`/inspector/deliveries/${d.id}`}
                        className="rounded-md border border-slate-300 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50"
                      >
                        Inspect
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </>
  );
}

function Stat({ label, value, valueClass, sub }: { label: string; value: string; valueClass?: string; sub?: string }) {
  return (
    <div className="px-6 py-6">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className={clsx('mt-1 tabular-nums', valueClass)}>{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}
